import type { ProductKind } from '@/types/product'

export const productKinds = [
  'package',
  'hot',
  'superior',
  'district',
  'course',
  'star',
  'gift',
] as const satisfies readonly ProductKind[]

export interface ProductTypeConfig {
  kind: ProductKind
  title: string
  listRoute: string
  createRoute: string
  editRoute: (id: string | number) => string
  permission: string
  importType?: number
  endpoints: {
    list: string
    create: string
    update: string
    detail: (id: string) => string
  }
  mainImageRatio?: number
  mainImageLimit?: number
  bannerImageRatio?: number
  skuEnabled?: boolean
}

export const productTypes = {
  package: {
    kind: 'package',
    importType: 0,
    title: '平替区商品',
    listRoute: '/product/list',
    createRoute: '/product/add',
    editRoute: (id) => `/product/edit/${id}`,
    permission: 'product:package',
    endpoints: {
      list: '/business/tbk/product/list',
      create: '/business/tbk/product/add',
      update: '/business/tbk/product/update',
      detail: (id) => `/business/tbk/product/detail/${id}`,
    },
    mainImageRatio: 1,
    mainImageLimit: 1,
    bannerImageRatio: 1,
    skuEnabled: true,
  },
  hot: {
    kind: 'hot',
    importType: 2,
    title: '限量购商品',
    listRoute: '/product/hotProduct/list',
    createRoute: '/product/hotProduct/add',
    editRoute: (id) => `/product/hotProduct/edit/${id}`,
    permission: 'product:hot',
    endpoints: {
      list: '/business/tbk/product/burstList',
      create: '/business/tbk/product/burstAdd',
      update: '/business/tbk/product/burstUpdate',
      detail: (id) => `/business/tbk/product/burstDetail/${id}`,
    },
    mainImageRatio: 1,
    mainImageLimit: 1,
    bannerImageRatio: 1,
    skuEnabled: true,
  },
  superior: {
    kind: 'superior',
    importType: 3,
    title: '自营商品',
    listRoute: '/product/superiorProduct/list',
    createRoute: '/product/superiorProduct/add',
    editRoute: (id) => `/product/superiorProduct/edit/${id}`,
    permission: 'product:superior',
    endpoints: {
      list: '/business/tbk/product/fineList',
      create: '/business/tbk/product/fineAdd',
      update: '/business/tbk/product/fineUpdate',
      detail: (id) => `/business/tbk/product/fineDetail/${id}`,
    },
    mainImageRatio: 1,
    mainImageLimit: 1,
    bannerImageRatio: 1,
    skuEnabled: true,
  },
  district: {
    kind: 'district',
    title: '区代商品',
    listRoute: '/product/districtProduct/list',
    createRoute: '/product/districtProduct/add',
    editRoute: (id) => `/product/districtProduct/edit/${id}`,
    permission: 'product:district',
    endpoints: {
      list: '/business/tbk/product/regionList',
      create: '/business/tbk/product/regionAdd',
      update: '/business/tbk/product/fineUpdate',
      detail: (id) => `/business/tbk/product/fineDetail/${id}`,
    },
  },
  course: {
    kind: 'course',
    title: '课程商品',
    listRoute: '/product/courseProduct/list',
    createRoute: '/product/courseProduct/add',
    editRoute: (id) => `/product/courseProduct/edit/${id}`,
    permission: 'product:course',
    endpoints: {
      list: '/business/tbk/product/courseList',
      create: '/business/tbk/product/courseAdd',
      update: '/business/tbk/product/courseUpdate',
      detail: (id) => `/business/tbk/product/courseDetail/${id}`,
    },
  },
  star: {
    kind: 'star',
    importType: 5,
    title: '9.9元商品',
    listRoute: '/product/starProduct/list',
    createRoute: '/product/starProduct/add',
    editRoute: (id) => `/product/starProduct/edit/${id}`,
    permission: 'product:star',
    endpoints: {
      list: '/business/tbk/product/starList',
      create: '/business/tbk/product/addStarShop',
      update: '/business/tbk/product/update',
      detail: (id) => `/business/tbk/product/starDetail/${id}`,
    },
    mainImageRatio: 1,
    mainImageLimit: 1,
    bannerImageRatio: 1,
    skuEnabled: true,
  },
  gift: {
    kind: 'gift',
    importType: 4,
    title: '零元购商品',
    listRoute: '/product/giftProduct/list',
    createRoute: '/product/giftProduct/add',
    editRoute: (id) => `/product/giftProduct/edit/${id}`,
    permission: 'product:gift',
    endpoints: {
      list: '/business/tbk/product/giftList',
      create: '/business/tbk/product/addGift',
      update: '/business/tbk/product/giftUpdate',
      detail: (id) => `/business/tbk/product/giftDetail/${id}`,
    },
    mainImageRatio: 1,
    mainImageLimit: 1,
    bannerImageRatio: 1,
    skuEnabled: true,
  },
} as const satisfies Partial<Record<ProductKind, ProductTypeConfig>>

export function getProductTypeConfig(kind: ProductKind): ProductTypeConfig {
  const config = productTypes[kind as keyof typeof productTypes]
  if (!config) throw new Error(`商品类型 ${kind} 尚未配置`)
  return config
}
